import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

interface SurveyAnswers {
  q1: string;
  q2: string;
  q3: string;
  q4: string;
  q5: string;
  q6: string;
  q7LiveWebinar: boolean;
  q7Email: string;
}

const Survey: React.FC = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Partial<SurveyAnswers>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const questions = [
    {
      id: 'q1',
      question: 'How do you currently see yourself?',
      options: [
        'Only a singer',
        'Aspiring music creator',
        'Independent artist (creating originals)',
        'Still figuring it out'
      ]
    },
    {
      id: 'q2',
      question: 'What is your biggest struggle right now?',
      options: [
        'Singing',
        'Composing a tune',
        'Lyrics',
        'Music Production / arrangement',
        'Finishing a full song',
        'Direction / clarity'
      ]
    },
    {
      id: 'q3',
      question: 'Have you ever released an original song?',
      options: [
        'No',
        'Yes, unofficially',
        'Yes, 1–2 songs',
        'Yes, multiple songs'
      ]
    },
    {
      id: 'q4',
      question: 'Which statement feels closest to your belief?',
      options: [
        'Singing alone is enough',
        'Singing + creation is important',
        "I'm confused about this",
        'I want to understand the modern path'
      ]
    },
    {
      id: 'q5',
      question: "What do you feel you're missing most?",
      options: [
        'Skill',
        'Confidence',
        'Clear system',
        'Guidance',
        'Time'
      ]
    },
    {
      id: 'q6',
      question: 'One honest line: What do you really want from your music journey?',
      type: 'text',
      optional: true
    },
    {
      id: 'q7',
      question: "Optional – FREE LIVE WEBINAR",
      description: "I'd like to attend a free live webinar on how singers can create and release their own original songs.",
      type: 'checkbox',
      optional: true
    }
  ];

  const totalQuestions = questions.length;

  const handleOptionSelect = (questionId: string, value: string) => {
    setAnswers({ ...answers, [questionId]: value });
    // Auto-advance to next question after selection (except for last questions)
    if (currentQuestion < totalQuestions - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
      }, 300);
    }
  };

  const handleTextChange = (questionId: string, value: string) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleCheckboxChange = (checked: boolean) => {
    setAnswers({ ...answers, q7LiveWebinar: checked });
    if (!checked) {
      // Clear email if unchecked
      setAnswers(prev => ({ ...prev, q7Email: '' }));
    }
  };

  const handleEmailChange = (value: string) => {
    setAnswers({ ...answers, q7Email: value });
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/save-survey-result', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...answers,
          timestamp: new Date().toISOString(),
        }),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('API endpoint not available. Please make sure you are running with "vercel dev" or the app is deployed to Vercel.');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit survey');
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error('Survey submission error:', err);
      setError(err.message || 'Failed to submit survey. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    const question = questions[currentQuestion];
    if (question.optional) return true;
    return answers[question.id as keyof SurveyAnswers] !== undefined && answers[question.id as keyof SurveyAnswers] !== '';
  };

  const isLastQuestion = currentQuestion === totalQuestions - 1;

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-amber-50 flex items-center justify-center p-4">
        <Helmet>
          <title>Thank You - RagaSpectrum Community Pulse Survey</title>
        </Helmet>
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="bg-gradient-to-r from-green-400 to-emerald-500 rounded-full p-4 w-20 h-20 mx-auto flex items-center justify-center">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            Your responses have been recorded. We appreciate you taking the time to share your music journey with us.
          </p>
          {answers.q7LiveWebinar && (
            <p className="text-sm text-orange-600 bg-orange-50 p-4 rounded-xl border border-orange-200">
              We'll send you the free live webinar details soon!
            </p>
          )}
          <p className="text-xs text-gray-400 mt-6">
            Powered by <a href="https://www.mukeshrathore.com" target="_blank" rel="noopener noreferrer" className="hover:text-orange-600 transition-colors">www.mukeshrathore.com</a>
          </p>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-amber-50 py-12 px-4">
      <Helmet>
        <title>RagaSpectrum Community Pulse Survey</title>
        <meta name="description" content="A 2-minute check-in to understand where you are in your music journey" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-4xl mx-auto">
        {/* Header Card */}
        <div className="bg-gradient-to-r from-orange-500 to-yellow-400 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white rounded-full p-2 shadow-lg">
              <img 
                src="/images/RagaSpectrum Logo 2025.png" 
                alt="RagaSpectrum Logo" 
                className="w-20 h-20 object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-center mb-3">
            RagaSpectrum Community Pulse Survey
          </h1>
          <p className="text-orange-50 text-center text-lg">
            A 2-minute check-in to understand where you are in your music journey.
          </p>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-orange-600">
                Question {currentQuestion + 1} of {totalQuestions}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round(((currentQuestion + 1) / totalQuestions) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-orange-500 to-yellow-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuestion + 1) / totalQuestions) * 100}%` }}
              />
          </div>
        </div>

        {/* Question */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {currentQ.question}
            {currentQ.optional && <span className="text-sm text-gray-500 ml-2">(Optional)</span>}
          </h2>

          {/* Multiple Choice Questions */}
          {currentQ.options && (
            <div className="space-y-3">
              {currentQ.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionSelect(currentQ.id, option)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all transform hover:scale-[1.02] ${
                    answers[currentQ.id as keyof SurveyAnswers] === option
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 hover:border-orange-300 hover:bg-orange-25 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      answers[currentQ.id as keyof SurveyAnswers] === option
                        ? 'border-orange-500'
                        : 'border-gray-300'
                    }`}>
                      {answers[currentQ.id as keyof SurveyAnswers] === option && (
                        <div className="w-3 h-3 rounded-full bg-orange-500" />
                      )}
                    </div>
                    <span className="text-gray-800">{option}</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Text Input Question */}
          {currentQ.type === 'text' && (
            <textarea
              value={(answers[currentQ.id as keyof SurveyAnswers] as string) || ''}
              onChange={(e) => handleTextChange(currentQ.id, e.target.value)}
              placeholder="Share your thoughts..."
              className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200 min-h-[120px] resize-none transition-all"
            />
          )}

          {/* Checkbox Question with Conditional Email */}
          {currentQ.type === 'checkbox' && (
            <div className="space-y-4">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="liveWebinar"
                  checked={answers.q7LiveWebinar || false}
                  onChange={(e) => handleCheckboxChange(e.target.checked)}
                  className="mt-1 mr-3 w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <label htmlFor="liveWebinar" className="text-gray-800 cursor-pointer">
                  {currentQ.description}
                </label>
              </div>

              {answers.q7LiveWebinar && (
                <div className="ml-8 mt-4 animate-fadeIn">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email (to send free live webinar details):
                    <span className="text-sm text-gray-500 ml-1">(optional but recommended)</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={answers.q7Email || ''}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    placeholder="your.email@example.com"
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleBack}
            disabled={currentQuestion === 0}
            className={`px-6 py-2 rounded-lg font-medium transition-all ${
              currentQuestion === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Back
          </button>

          {!isLastQuestion ? (
            <button
              onClick={handleNext}
              disabled={!canProceed() || !!currentQ.options} // Auto-advance for options, manual for text/checkbox
              className={`px-6 py-3 rounded-xl font-medium transition-all ${
                canProceed() && !currentQ.options
                  ? 'bg-orange-600 text-white hover:bg-orange-700 shadow-md hover:shadow-lg'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-8 py-3 bg-gradient-to-r from-orange-600 to-yellow-500 text-white rounded-xl font-medium hover:from-orange-700 hover:to-yellow-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              {submitting ? 'Submitting...' : 'Submit Survey'}
            </button>
          )}
        </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default Survey;
